import { useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';

export interface Permissions {
  dashboard: boolean;
  contatos: boolean;
  agenda: boolean;
  crm: boolean;
  chat: boolean;
  campanhas: boolean;
  assistentes: boolean;
  relatorios: boolean;
  configuracoes: boolean;
}

export type PermissionArea = keyof Permissions;

export function usePermissions() {
  const { user } = useAuth();

  const permissions = useMemo<Permissions>(() => {
    // Se não há usuário, retornar permissões vazias
    if (!user) {
      return {
        dashboard: false,
        contatos: false,
        agenda: false,
        crm: false,
        chat: false,
        campanhas: false,
        assistentes: false,
        relatorios: false,
        configuracoes: false
      };
    }

    // Usuários do tipo "agency_user" (vindos da tabela agency_users) têm acesso total a tudo
    if (user.type === 'agency_user' || user.profile === 'agency') {
      console.log('✅ [usePermissions] Agency user detected - full access granted:', {
        type: user.type,
        profile: user.profile,
        email: user.email
      });

      return {
        dashboard: true,
        contatos: true,
        agenda: true,
        crm: true,
        chat: true,
        campanhas: true,
        assistentes: true,
        relatorios: true,
        configuracoes: true
      };
    }

    // Para account_users, usar o campo role
    const roleData = (user as any)?.role;

    if (!roleData) {
      console.warn('[usePermissions] No role found for account_user:', user.email);
      return {
        dashboard: false,
        contatos: false,
        agenda: false,
        crm: false,
        chat: false,
        campanhas: false,
        assistentes: false,
        relatorios: false,
        configuracoes: false
      };
    }

    try {
      const parsed = typeof roleData === 'string' ? JSON.parse(roleData) : roleData;

      return {
        dashboard: parsed.dashboard === true,
        contatos: parsed.contatos === true,
        agenda: parsed.agenda === true,
        crm: parsed.crm === true,
        chat: parsed.chat === true,
        campanhas: parsed.campanhas === true,
        assistentes: parsed.assistentes === true,
        relatorios: parsed.relatorios === true,
        configuracoes: parsed.configuracoes === true
      };
    } catch (error) {
      console.error('[usePermissions] Error parsing user permissions:', error);
      return {
        dashboard: false,
        contatos: false,
        agenda: false,
        crm: false,
        chat: false,
        campanhas: false,
        assistentes: false,
        relatorios: false,
        configuracoes: false
      };
    }
  }, [user, user?.type, user?.profile, (user as any)?.role]);

  const hasPermission = (area: PermissionArea): boolean => {
    return permissions[area] === true;
  };

  const hasAnyPermission = (...areas: PermissionArea[]): boolean => {
    return areas.some(area => hasPermission(area));
  };

  const hasAllPermissions = (...areas: PermissionArea[]): boolean => {
    return areas.every(area => hasPermission(area));
  };

  const isAdmin = (): boolean => {
    return hasPermission('configuracoes');
  };

  const allowedAreas = useMemo<PermissionArea[]>(() => {
    return (Object.keys(permissions) as PermissionArea[]).filter(
      area => permissions[area] === true
    );
  }, [permissions]);

  return {
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isAdmin,
    allowedAreas
  };
}
